import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth } from '../services/api';
import { Button } from '../components/ui';

export default function Login() {
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [referrer, setReferrer] = useState('');
  const [isLinkedReferrer, setIsLinkedReferrer] = useState(false);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [referrerLookupError, setReferrerLookupError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, autoLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isReferrerLocked = isLinkedReferrer || Boolean(referrerInfo);

  useEffect(() => {
    if (autoLogin) {
      navigate('/dashboard');
    }
  }, [autoLogin, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const register = params.get('register');
    const referrerFromLink = params.get('referrer');
    if (register === '1') {
      setIsRegister(true);
    }
    if (referrerFromLink) {
      setReferrer(referrerFromLink);
      setIsLinkedReferrer(true);
    } else {
      setIsLinkedReferrer(false);
    }
  }, [location.search]);

  useEffect(() => {
    if (!isRegister) {
      setReferrerInfo(null);
      setReferrerLookupError('');
      return;
    }

    const normalizedReferrer = referrer.trim();
    if (!normalizedReferrer) {
      setReferrerInfo(null);
      setReferrerLookupError('');
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await auth.getReferrer(normalizedReferrer);
        if (cancelled) return;
        setReferrerInfo(res.data.referrer);
        setReferrerLookupError('');
      } catch (err) {
        if (cancelled) return;
        setReferrerInfo(null);
        setReferrerLookupError(err.response?.data?.error || 'Unable to resolve referrer');
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isRegister, referrer]);

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);
    try {
      await login(username, password, rememberMe);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    resetMessages();

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!referrer) {
      setError('Referrer is required');
      return;
    }

    if (!referrerInfo) {
      setError(referrerLookupError || 'Referrer not found. Please enter a valid referrer username.');
      return;
    }

    setLoading(true);
    try {
      await auth.register({
        username,
        password,
        displayName,
        referrer
      });
      setSuccess('Registration successful! Please login with your credentials.');
      setIsRegister(false);
      setUsername('');
      setPassword('');
      setConfirmPassword('');
      setDisplayName('');
      setReferrer('');
      setReferrerInfo(null);
      setReferrerLookupError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#09111f] text-slate-100">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-[-18rem] h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-blue-300/16 blur-3xl" />
        <div className="absolute bottom-[-8rem] right-[-4rem] h-[20rem] w-[20rem] rounded-full bg-emerald-300/10 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-4 py-10">
        <section className="surface-card-elevated mx-auto w-full max-w-md">
          <div className="mb-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-[20px] bg-blue-300/15 text-blue-100 shadow-[0_12px_30px_rgba(20,40,80,0.25)]">
              <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="10" r="6" strokeWidth="1.7" />
                <path d="M10 12 Q12 14, 14 12" strokeWidth="1.7" strokeLinecap="round" />
                <path d="M5 14 Q12 18, 19 14" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </div>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-50">Octopus</h2>
          </div>

          <div className="mb-5 flex rounded-full bg-slate-900/70 p-1">
            <button
              type="button"
              onClick={() => { setIsRegister(false); resetMessages(); }}
              className={`min-h-[44px] flex-1 rounded-full px-4 text-sm font-medium transition-all ${!isRegister ? 'bg-blue-300 text-slate-950 shadow-sm' : 'text-slate-300'}`}
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => { setIsRegister(true); resetMessages(); }}
              className={`min-h-[44px] flex-1 rounded-full px-4 text-sm font-medium transition-all ${isRegister ? 'bg-blue-300 text-slate-950 shadow-sm' : 'text-slate-300'}`}
            >
              Register
            </button>
          </div>

          {error ? (
            <div className="mb-4 rounded-[18px] border border-red-300/18 bg-red-300/10 px-4 py-3 text-sm text-red-100">
              {error}
            </div>
          ) : null}
          {success ? (
            <div className="mb-4 rounded-[18px] border border-emerald-300/18 bg-emerald-300/10 px-4 py-3 text-sm text-emerald-100">
              {success}
            </div>
          ) : null}

          {isRegister ? (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="label">Username</label>
                <input type="text" name="username" value={username} onChange={(e) => setUsername(e.target.value)} className="input" required />
              </div>
              <div>
                <label className="label">Display Name</label>
                <input type="text" name="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="input" required />
              </div>
              {!isReferrerLocked ? (
                <div>
                  <label className="label">Referrer</label>
                  <input type="text" name="referrer" value={referrer} onChange={(e) => setReferrer(e.target.value)} placeholder="Referrer username" className="input" required />
                </div>
              ) : null}
              {referrerInfo ? (
                <div className="rounded-[18px] border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-slate-300" data-testid="referrer-display-name">
                  {`You are referred by ${referrerInfo.displayName || '--'}.`}
                </div>
              ) : null}
              {referrerLookupError ? <p className="text-sm text-red-200">{referrerLookupError}</p> : null}
              <div>
                <label className="label">Password</label>
                <input type="password" name="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input" required />
              </div>
              <div>
                <label className="label">Confirm Password</label>
                <input type="password" name="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="input" required />
              </div>
              <Button type="submit" variant="primary" className="w-full justify-center" disabled={loading}>
                {loading ? 'Creating...' : 'Create Account'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="label">Username</label>
                <input type="text" name="username" value={username} onChange={(e) => setUsername(e.target.value)} className="input" required />
              </div>
              <div>
                <label className="label">Password</label>
                <input type="password" name="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input" required />
              </div>
              <label className="flex items-center gap-3 rounded-[18px] border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-slate-300">
                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                Remember me
              </label>
              <Button type="submit" variant="primary" className="w-full justify-center" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
